import * as React from 'react';
import moment from 'moment';
import Select from 'antd/lib/select';
import withStyles from 'react-jss';
import { Document } from 'src/components/pages/Container/Interfaces/TypesInShipmentTracking';
import { CommonProps } from './common.intefaces';
import { StylesProps } from 'src/Library/Types';
import { commonStyles } from 'src/Library/CommonStyles';
import { fileUploadOnS3 } from 'src/api/inquiry';
import { uploadImageToS3 } from 'src/api/salesorder';
import { getFileExtension } from 'src/utils/utils';
import Icon from 'antd/lib/icon';
import Spin from 'antd/lib/spin';
import Upload from 'antd/lib/upload';
import Button from 'antd/lib/button';
import message from 'antd/lib/message';
import Tooltip from 'antd/lib/tooltip';
import FormItem from 'antd/es/form/FormItem';

interface IProps extends CommonProps, StylesProps<ReturnType<typeof styles>> {
  document: Document;
  showLabel: boolean;
  showDelete: boolean;
  removeUpload: () => void;
  onChange: (val: Document) => void;
  handleRemove: () => void;
  allowedDocumentTypes: Record<string, string>;
  additionalStyles: Record<string, Record<string, string>>
}

const styles = (theme) => ({
  ...commonStyles(theme),
  label: {
    width: '29%',
    color: '#262626',
    fontSize: 12,
    fontWeight: 600,
  },
  formItem: {
    '& .ant-form-item': {
      width: '70%',
    },
    '& .ant-form-item-control-wrapper': {
      width: '100%',
    },
    '& .ant-form-item-control': {
      lineHeight: 1.3,
    },
  },
});

const SelectDocuments = (props: IProps) => {

  const {
    mode,
    classes,
    document,
    onChange,
    removeUpload,
    showLabel,
    showDelete,
    placeholder,
    handleRemove,
    allowedDocumentTypes,
    additionalStyles,
  } = props;
  
  const [uploading, setUploading] = React.useState<boolean>(false);

  const renderDocumentOptions = () => {
    return Object.keys(allowedDocumentTypes).map(key => <Select.Option
      key={key}
    >
      {allowedDocumentTypes[key]}
    </Select.Option>) || [];
  };

  const handleTypeChange = (type: string) => {
    onChange({
      ...document,
      type,
    });
  };

  const showError = () => {
    return Boolean(document?.url && !document?.type);
  }

  const renderDocumentType = () => {
    return <Select
      style={{
        width: 120,
        ...(additionalStyles?.selectStyles || {}),
      }}
      allowClear
      showSearch
      value={document?.type}
      onChange={handleTypeChange}
      mode={mode || 'default'}
      dropdownMatchSelectWidth={false}
      placeholder={placeholder || 'Document Type'}
      getPopupContainer={(trigger: any) => trigger.parentNode}
    >
      {renderDocumentOptions()}
    </Select>;
  };

  const s3LinkRequest = async ({ file }) => {
    if (uploading) {
      return;
    }
    setUploading(true);
    const extension = getFileExtension(file.name);
    const response = await fileUploadOnS3({
      extension,
      fileName: file.name,
      document_type: file.type,
      initialPath: 'track',
    });
    if (response.isSuccess) {
      const upload = await uploadImageToS3(file, response.data.post, extension);
      if (upload.isSuccess) {
        onChange({
          ...document,
          name: file.name,
          url: response.data.get,
          addedAt: moment().valueOf(),
        });
      }
    } else {
      message.error(response.errorMessage);
    }
    setUploading(false);
  }

  const renderUploadButton = () => {
    return <Upload
      style={{ width: '15%' }}
      showUploadList={false}
      multiple={false}
      customRequest={s3LinkRequest}
    >
      <Button
        disabled={uploading}
        style={{ marginLeft: 10 }}
      >
        <Icon type="upload"/> Upload
      </Button>
    </Upload>;
  };

  const renderDocument = () => {
    if (uploading) {
      return <Spin
        size="small"
        style={{
          width: '20%',
          maxWidth: '20%',
          marginLeft: 10,
        }}
      />;
    }
    if (!document?.url) return null;
    return <a
      href={document?.url}
      className={classes.ellipses}
      style={{
        width: '20%',
        maxWidth: '20%',
        marginLeft: 10,
      }}
    >
      {document?.name || ''}
    </a>;
  };

  const renderDocumentError = () => {
    if (!document?.url && !document?.type) return null;
    if (document?.url && document?.type) return null;
    return <span
      style={{
        color: 'red',
        marginLeft: '29%',
        fontSize: 10,
        marginTop: -15,
        ...(additionalStyles?.errorStyles || {}),
      }}
    >
      Type and Document are Required
    </span>;
  };

  const renderRemoveUpload = () => {
    if (document?.name && document.url) {
      return <Tooltip
        title="Remove Document"
      >
        <Icon
          type="close-circle"
          onClick={removeUpload}
        />
      </Tooltip>
    };
    return null;
  }

  const renderRemove = () => {
    if (!showDelete) return null;
    return <Tooltip
      title="Delete this row"
    >
      <Icon
        type="delete"
        style={{
          color: 'red',
          marginLeft: 20,
        }}
        onClick={handleRemove}
      />
    </Tooltip>;
  };

  const renderLabel = () => {
    return <div
      className={classes.label}
      style={{
        ...(additionalStyles?.labelStyles || {}),
      }}
    >
      {showLabel && 'Documents'}
    </div>;
  };

  return <FormItem
    className={classes.formItem}
    validateStatus={showError() ? "error" : "success"}
    style={{
      ...props.style,
      display: 'flex',
      flexDirection: 'row',
    }}
  >
    <div
      className={classes.flexRow}
    >
      {renderLabel()}
      {renderDocumentType()}
      {renderUploadButton()}
      {renderDocument()}
      {renderRemoveUpload()}
      {renderRemove()}
    </div>
    {renderDocumentError()}
  </FormItem>;
};

const SelectDocumentStyled = withStyles(styles)(SelectDocuments);
export default React.memo(SelectDocumentStyled);
